// A tiny external store bridging the CANpark "Register a plate" tour's
// action-driven steps to the plate-registration modal (which lives deep inside a
// per-lot card, so the tour's sync hook can't read its local state directly).
// The modal/form publish here; useRegisterPlateTourSync (mounted once at the
// CANparkAdmin level) reads it. Single-instance app, so a module-level store is
// fine.

type Listener = () => void;

interface RegisterPlateTourState {
	/** The "adding" form (license-plate fields) is mounted and visible. */
	formOpen: boolean;
	/** The sandboxed save has run (no real plates were written). */
	saved: boolean;
}

let state: RegisterPlateTourState = { formOpen: false, saved: false };
const listeners = new Set<Listener>();

const emit = (): void => {
	listeners.forEach((l) => l());
};

/**
 * Publish whether the adding form is open. Opening it also clears any previous
 * `saved` flag so a fresh run starts clean.
 */
export const setRegisterPlateFormOpen = (open: boolean): void => {
	const next: RegisterPlateTourState = open
		? { formOpen: true, saved: false }
		: { ...state, formOpen: false };
	if (state.formOpen === next.formOpen && state.saved === next.saved) return;
	state = next;
	emit();
};

/** Publish that the sandboxed save has completed. */
export const signalRegisterPlateSaved = (): void => {
	if (state.saved) return;
	state = { ...state, saved: true };
	emit();
};

export const subscribeRegisterPlateTour = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const getRegisterPlateTourSnapshot = (): RegisterPlateTourState => state;
